import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/site/Layout";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Pricing() {
  const [data, setData] = useState({ tiers: {}, packs: {} });
  const [busy, setBusy] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { api.get("/pricing").then(r => setData(r.data)); }, []);

  const checkout = async (kind, id) => {
    if (!user) { nav("/login"); return; }
    setBusy(id);
    try {
      const body = { kind, origin_url: window.location.origin };
      if (kind === "subscription") body.tier_id = id; else body.pack_id = id;
      const { data } = await api.post("/checkout/create", body);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Checkout failed");
      setBusy(null);
    }
  };

  const tierEntries = Object.entries(data.tiers);
  const packEntries = Object.entries(data.packs);

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-16 pb-24">
        <div className="overline mb-3">Membership</div>
        <h1 className="font-serif-editorial text-5xl md:text-7xl tracking-tighter mb-16 max-w-4xl">
          Choose your appetite.
        </h1>

        <div className="grid md:grid-cols-4 gap-px bg-white/10 mb-24">
          {tierEntries.map(([id, t], i) => (
            <div key={id} className="bg-black p-8 flex flex-col" data-testid={`tier-card-${id}`}>
              <div className="overline">Tier {String(i+1).padStart(2,'0')}</div>
              <div className="font-serif-editorial text-3xl mt-3">{t.name}</div>
              <div className="mt-6 font-serif-editorial text-5xl">
                ${t.price}<span className="text-sm text-neutral-500 font-mono-ui">/mo</span>
              </div>
              <div className="mt-2 text-neutral-400 text-sm font-mono-ui">{t.credits} credits · monthly</div>
              <ul className="mt-6 space-y-2 text-sm text-neutral-500 flex-1">
                <li>· {t.credits} unlocks or {Math.floor(t.credits/20)} full galleries</li>
                <li>· Credits reset monthly (no rollover)</li>
                <li>· Voting privileges</li>
              </ul>
              <button
                disabled={busy === id}
                onClick={() => checkout("subscription", id)}
                className="btn-white mt-8 justify-center"
                data-testid={`subscribe-${id}`}>
                {busy === id ? "..." : "Subscribe"}
              </button>
            </div>
          ))}
        </div>

        <div className="border-t border-white/10 pt-16">
          <div className="overline mb-3">One-time · Credit packs</div>
          <h2 className="font-serif-editorial text-4xl tracking-tighter mb-10">Top up without commitment.</h2>
          <div className="grid md:grid-cols-3 gap-px bg-white/10">
            {packEntries.map(([id, p]) => (
              <div key={id} className="bg-black p-8 flex flex-col" data-testid={`pack-card-${id}`}>
                <div className="font-serif-editorial text-3xl">{p.name}</div>
                <div className="mt-4 font-serif-editorial text-4xl">${p.price}</div>
                <div className="mt-2 text-neutral-500 text-sm font-mono-ui">{p.credits} credits</div>
                <button
                  disabled={busy === id}
                  onClick={() => checkout("pack", id)}
                  className="btn-ghost mt-8 justify-center"
                  data-testid={`buy-pack-${id}`}>
                  {busy === id ? "..." : "Purchase"}
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Layout>
  );
}
